// ── Filtros y agrupación sobre propiedades de Notion ─────────────────────────
//
// Todo se evalúa en el navegador contra la copia local (`page.properties`), que
// la sincronización deja completa. No se le pregunta nada a Notion: filtrar es
// instantáneo y no gasta cuota de API.
//
// Un filtro es { prop, op, value }. El operador dice qué se compara y de qué
// forma; los operadores válidos dependen del TIPO de la propiedad, para que no
// se pueda pedir "contiene" sobre una casilla ni "es verdadero" sobre un texto.

// Familias de tipos, que es lo que de verdad decide los operadores
const TEXTO   = ["title", "rich_text", "url", "email", "phone_number", "unique_id", "created_by", "last_edited_by"];
const NUMERO  = ["number"];
const OPCION  = ["select", "status"];
const VARIAS  = ["multi_select"];
const GENTE   = ["people"];
const FECHA   = ["date", "created_time", "last_edited_time"];
const CASILLA = ["checkbox"];
const VINCULO = ["relation"];

export function familia(type) {
  if (TEXTO.includes(type))   return "texto";
  if (NUMERO.includes(type))  return "numero";
  if (OPCION.includes(type))  return "opcion";
  if (VARIAS.includes(type))  return "varias";
  if (GENTE.includes(type))   return "gente";
  if (FECHA.includes(type))   return "fecha";
  if (CASILLA.includes(type)) return "casilla";
  if (VINCULO.includes(type)) return "vinculo";
  return "texto";                 // formula, rollup y files se tratan como texto
}

// Operadores por familia. `valor` dice qué pide el operador al usuario:
// "texto" | "numero" | "fecha" | "opciones" (varias de una lista) | null (nada).
const OPS = {
  texto: [
    { op: "contiene",    label: "contiene",       valor: "texto" },
    { op: "no_contiene", label: "no contiene",    valor: "texto" },
    { op: "es",          label: "es exactamente", valor: "texto" },
    { op: "empieza",     label: "empieza por",    valor: "texto" },
    { op: "vacio",       label: "está vacío",     valor: null },
    { op: "no_vacio",    label: "tiene valor",    valor: null },
  ],
  numero: [
    { op: "igual",    label: "=",           valor: "numero" },
    { op: "distinto", label: "≠",           valor: "numero" },
    { op: "mayor",    label: ">",           valor: "numero" },
    { op: "menor",    label: "<",           valor: "numero" },
    { op: "mayor_ig", label: "≥",           valor: "numero" },
    { op: "menor_ig", label: "≤",           valor: "numero" },
    { op: "vacio",    label: "está vacío",  valor: null },
    { op: "no_vacio", label: "tiene valor", valor: null },
  ],
  opcion: [
    { op: "es_alguno", label: "es",          valor: "opciones" },
    { op: "no_es",     label: "no es",       valor: "opciones" },
    { op: "vacio",     label: "está vacío",  valor: null },
    { op: "no_vacio",  label: "tiene valor", valor: null },
  ],
  varias: [
    { op: "tiene_alguno", label: "incluye alguno de", valor: "opciones" },
    { op: "tiene_todos",  label: "incluye todos",     valor: "opciones" },
    { op: "no_tiene",     label: "no incluye",        valor: "opciones" },
    { op: "vacio",        label: "está vacío",        valor: null },
    { op: "no_vacio",     label: "tiene valor",       valor: null },
  ],
  gente: [
    { op: "tiene_alguno", label: "incluye a",   valor: "opciones" },
    { op: "no_tiene",     label: "no incluye a", valor: "opciones" },
    { op: "vacio",        label: "sin asignar",  valor: null },
    { op: "no_vacio",     label: "asignada",     valor: null },
  ],
  fecha: [
    { op: "es",       label: "es el día",   valor: "fecha" },
    { op: "antes",    label: "antes de",    valor: "fecha" },
    { op: "despues",  label: "después de",  valor: "fecha" },
    { op: "hoy",      label: "es hoy",      valor: null },
    { op: "vencida",  label: "ya pasó",     valor: null },
    { op: "proximos", label: "próximos días", valor: "numero" },
    { op: "vacio",    label: "está vacía",  valor: null },
    { op: "no_vacio", label: "tiene fecha", valor: null },
  ],
  casilla: [
    { op: "marcada",    label: "está marcada",    valor: null },
    { op: "no_marcada", label: "no está marcada", valor: null },
  ],
  vinculo: [
    { op: "tiene_alguno", label: "incluye",     valor: "opciones" },
    { op: "no_tiene",     label: "no incluye",  valor: "opciones" },
    { op: "vacio",        label: "sin vínculos", valor: null },
    { op: "no_vacio",     label: "con vínculos", valor: null },
  ],
};

export const operadoresDe = (type) => OPS[familia(type)] || OPS.texto;
export const operador = (type, op) => operadoresDe(type).find(o => o.op === op) || operadoresDe(type)[0];

// Toda propiedad se puede filtrar; solo estas se pueden usar para AGRUPAR, que
// es lo que da columnas discretas a un tablero.
export const agrupables = (schema = []) =>
  schema.filter(p => ["select", "status", "checkbox", "multi_select", "people"].includes(p.type));

// Valores de una propiedad, como lista de textos comparables
function valoresDe(entry) {
  const v = entry?.value;
  if (v === null || v === undefined || v === "") return [];
  if (Array.isArray(v)) return v.map(x => (typeof x === "object" ? (x.name ?? x.id ?? "") : String(x))).filter(Boolean);
  if (typeof v === "object") return v.start ? [String(v.start)] : [];
  return [String(v)];
}

const texto = (entry) => valoresDe(entry).join(" ").toLowerCase();
const dia   = (entry) => { const [d] = valoresDe(entry); return d ? d.slice(0, 10) : null; };
const hoy   = () => new Date().toISOString().slice(0, 10);

function sumaDias(n) {
  const d = new Date();
  d.setDate(d.getDate() + (parseInt(n, 10) || 0));
  return d.toISOString().slice(0, 10);
}

// ¿Cumple una página una condición?
export function cumple(page, filtro) {
  const entry = page.properties?.[filtro.prop];
  const vals  = valoresDe(entry);
  const val   = filtro.value;
  const lista = (Array.isArray(val) ? val : val != null && val !== "" ? [val] : []).map(String);
  const bajo  = vals.map(v => v.toLowerCase());

  switch (filtro.op) {
    // comunes
    case "vacio":       return vals.length === 0;
    case "no_vacio":    return vals.length > 0;

    // texto
    case "contiene":    return texto(entry).includes(String(val ?? "").toLowerCase());
    case "no_contiene": return !texto(entry).includes(String(val ?? "").toLowerCase());
    case "empieza":     return texto(entry).startsWith(String(val ?? "").toLowerCase());
    case "es":
      // "es" vale para texto, opción y fecha: se compara el valor plano
      if (!lista.length) return false;
      return bajo.includes(String(val).toLowerCase()) || dia(entry) === String(val).slice(0, 10);

    // opción / multi / gente / vínculo
    case "es_alguno":    return lista.some(v => bajo.includes(v.toLowerCase()));
    case "no_es":        return !lista.some(v => bajo.includes(v.toLowerCase()));
    case "tiene_alguno": return lista.some(v => bajo.includes(v.toLowerCase()));
    case "tiene_todos":  return lista.every(v => bajo.includes(v.toLowerCase()));
    case "no_tiene":     return !lista.some(v => bajo.includes(v.toLowerCase()));

    // número
    case "igual":    return Number(vals[0]) === Number(val);
    case "distinto": return Number(vals[0]) !== Number(val);
    case "mayor":    return vals.length > 0 && Number(vals[0]) >  Number(val);
    case "menor":    return vals.length > 0 && Number(vals[0]) <  Number(val);
    case "mayor_ig": return vals.length > 0 && Number(vals[0]) >= Number(val);
    case "menor_ig": return vals.length > 0 && Number(vals[0]) <= Number(val);

    // fecha
    case "antes":    return !!dia(entry) && dia(entry) <  String(val).slice(0, 10);
    case "despues":  return !!dia(entry) && dia(entry) >  String(val).slice(0, 10);
    case "hoy":      return dia(entry) === hoy();
    case "vencida":  return !!dia(entry) && dia(entry) < hoy();
    case "proximos": return !!dia(entry) && dia(entry) >= hoy() && dia(entry) <= sumaDias(val || 7);

    // casilla
    case "marcada":    return entry?.value === true;
    case "no_marcada": return entry?.value !== true;

    default: return true;
  }
}

// Aplica la lista de filtros. modo "todos" = AND (por defecto), "alguno" = OR.
export function aplicarFiltros(pages, filtros = [], modo = "todos") {
  const activos = filtros.filter(f => f?.prop && f?.op);
  if (!activos.length) return pages;
  return pages.filter(p => modo === "alguno"
    ? activos.some(f => cumple(p, f))
    : activos.every(f => cumple(p, f)));
}

// Texto corto de un filtro, para las pastillas de la barra
export function describeFiltro(filtro, schema = []) {
  const def = schema.find(p => p.name === filtro.prop);
  const op  = operador(def?.type || "rich_text", filtro.op);
  const val = Array.isArray(filtro.value) ? filtro.value.join(", ") : filtro.value;
  return `${filtro.prop} ${op.label}${op.valor && val != null && val !== "" ? ` ${val}` : ""}`;
}

export const SIN_VALOR = "Sin valor";

// Agrupa páginas por una propiedad. Devuelve [{ key, label, color, pages }].
// Con multi_select o people una página cae en varios grupos, igual que en Notion.
export function agrupar(pages, def) {
  const grupos = new Map();
  const add = (key, page) => {
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(page);
  };

  // Las opciones del esquema primero, para que una columna vacía siga existiendo
  // y se pueda soltar una tarjeta en ella.
  if (def?.options?.length) for (const o of def.options) grupos.set(o.name, []);
  if (def?.type === "checkbox") { grupos.set("Marcadas", []); grupos.set("Sin marcar", []); }

  for (const page of pages) {
    const entry = page.properties?.[def?.name];
    if (def?.type === "checkbox") { add(entry?.value === true ? "Marcadas" : "Sin marcar", page); continue; }
    const vals = valoresDe(entry);
    if (!vals.length) { add(SIN_VALOR, page); continue; }
    for (const v of vals) add(v, page);
  }

  return [...grupos.entries()].map(([key, pgs]) => ({
    key,
    label: key,
    color: def?.options?.find(o => o.name === key)?.color || null,
    pages: pgs,
  }));
}

// Solo select y status se pueden cambiar arrastrando: son de valor único y
// escribibles. Con multi_select o people, mover una tarjeta sería ambiguo.
export const agrupacionArrastrable = (def) =>
  !!def && (def.type === "select" || def.type === "status") && def.editable;

// Orden por una propiedad, con los vacíos siempre al final
export function ordenar(pages, prop, dir = "asc") {
  if (!prop) return pages;
  const mul = dir === "desc" ? -1 : 1;
  const key = (p) => {
    if (prop === "__title") return (p.title || "").toLowerCase();
    const entry = p.properties?.[prop];
    const v = entry?.value;
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "number")  return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    const vals = valoresDe(entry);
    return vals.length ? vals.join(", ").toLowerCase() : null;
  };
  return pages.slice().sort((a, b) => {
    const ka = key(a), kb = key(b);
    if (ka === null) return kb === null ? 0 : 1;
    if (kb === null) return -1;
    if (ka === kb) return 0;
    return ka > kb ? mul : -mul;
  });
}
